import { Box } from '@mui/material';
import { ActivityScene } from '@/contexts/ActivityContext';
import CreateActivityHeader from '@/components/activity/createActivity/CreateActivityHeader';
import CreateActivityFooter from '@/components/activity/createActivity/CreateActivityFooter';
import ActivityFormScene from '@/contexts/ActivityFormContext';
import CreateActivityContent from '@/components/activity/createActivity/CreateActivityContent';
import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@/contexts/AppContext';
import { SupervisorContext } from 'src/contexts/SupervisorContext';
import { useActivePersona } from '@/personas/useActivePersona';
import { useClientPersona } from '@/personas/useClientPersona';
import { resolveChildPresentation } from './childPresentation';
import { ChildActivityFormsDefs } from '@/contexts/childActivityFormsDefs';
import {
  ChildStarActivityFormsDefs,
  StarActivityFormsDefs,
  TeenStarActivityFormsDefs,
} from '@/contexts/starActivityFormsDefs';
import { useStarValuePersona } from '@/personas/useStarValuePersona';
import { useCharacter } from '@/contexts/CharacterContext';
import { CharacterMood } from '@/components/character';

// Kompanjonens fråga + humör per wizard-steg (barnläge). Steget härleds från
// :formId, som mappar direkt mot ActivityForms-nycklarna (Title/Date/Duration).
const STEP_COMPANION: Record<string, { key: string; mood: CharacterMood }> = {
  Title: { key: 'child.companion.createStep1', mood: 'thinking' },
  Date: { key: 'child.companion.createStep3', mood: 'thinking' },
  Duration: { key: 'child.companion.createStep2', mood: 'happy' },
  StarValue: { key: 'child.companion.createStep4', mood: 'thinking' },
};

const CreateActivityPage = () => {
  const { setShowNavbar, pageHeight } = useContext(AppContext);
  const { supervisorHeaderHeight, currentClient, isSupervising } = useContext(SupervisorContext);
  const { formId } = useParams();
  const { t } = useTranslation();
  const { setMood, setCompanionSpeech } = useCharacter();

  // Barn-presentationen (barn-copy + kompanjon som läser stegen) styrs av PERSONA,
  // begränsat till 'child' — teen ska INTE få barn-presentationen. Endast
  // presentationen byts; formId/nextForm/additionalForm/nextFormAction är
  // identiska i ChildActivityFormsDefs, så AssistantButton + MoreOptions bevaras.
  //   - Egen vy: brukarens egen persona (useActivePersona).
  //   - Handledarläge: KLIENTENS persona avgör (useClientPersona), inte
  //     handledarens (som alltid resolvar 'adult').
  const ownProfile = useActivePersona({ currentClient, isSupervising });
  const { persona: clientPersona } = useClientPersona(currentClient);
  const isChildPresentation = resolveChildPresentation({
    isSupervising,
    ownPersona: ownProfile.persona,
    clientPersona,
  });

  // Stjärnvärdessteget (steg 4) läggs på för personas som HAR stjärnor — barn och
  // ungdom, samt handledaren när hen skapar åt en sådan klient. Vuxenvyn får
  // oförändrad 3-stegswizard. Barn-copyn bevaras genom att steget läggs OVANPÅ
  // ChildActivityFormsDefs i stället för att ersätta den.
  const { enabled: starValueEnabled, asXp } = useStarValuePersona();

  const resolveFormsConfig = () => {
    if (isChildPresentation) {
      return starValueEnabled ? ChildStarActivityFormsDefs : ChildActivityFormsDefs;
    }
    if (!starValueEnabled) {
      return undefined;
    }
    // asXp = tonåringens egen vy → knapptexten till steget talar XP, inte stjärnor.
    return asXp ? TeenStarActivityFormsDefs : StarActivityFormsDefs;
  };

  useEffect(() => {
    setShowNavbar(false);
    return () => setShowNavbar(true);
  }, []);

  // Kompanjonen ställer stegets fråga (endast barnläge). Nollställs vid
  // unmount/lägesbyte så att bubblan inte fastnar i andra vyer.
  useEffect(() => {
    if (!isChildPresentation) {
      return;
    }
    const step = formId ? STEP_COMPANION[formId] : undefined;
    if (!step) {
      return;
    }
    setMood(step.mood);
    setCompanionSpeech(t(step.key) ?? '');
    return () => {
      setCompanionSpeech('');
      setMood('idle');
    };
  }, [isChildPresentation, formId, setMood, setCompanionSpeech, t]);

  return (
    <ActivityScene useStoredActivity>
      <ActivityFormScene formOverrideConfig={resolveFormsConfig()}>
        <Box
          sx={{
            minHeight: `calc(${pageHeight}px - ${supervisorHeaderHeight}px)`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CreateActivityHeader />
          <CreateActivityContent />
          <CreateActivityFooter />
        </Box>
      </ActivityFormScene>
    </ActivityScene>
  );
};

export default CreateActivityPage;
